import React, { useCallback, useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid2,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';

import ImageUploadBox from '~/components/CustomerCommunication/HeroPlanner/Components/ScheduleForms/Inputs/ImageUploadBox';

import useImageUrlGenerator from '~/hooks/useImageUrlGenerator';

import CorporateEventsService, { CreateEventParams, Event } from '~/services/CorporateEventsService';
import { uploadImage } from '~/services/ImageService';
import { Dayjs, newDate } from '~/services/TimeService';

interface EventFormProps {
  event?: Event;
  onSubmit: (data: CreateEventParams) => Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
  onHasUnsavedChangesChange?: (hasChanges: boolean) => void;
}

export default function EventForm({
  event,
  onSubmit,
  onCancel,
  isSubmitting = false,
  onHasUnsavedChangesChange,
}: EventFormProps) {
  const { enqueueSnackbar } = useSnackbar();
  const { data: companies = [] } = useQuery({
    queryKey: ['corporate-events', 'companies'],
    queryFn: () => CorporateEventsService.getCompanies(),
  });

  const [formData, setFormData] = useState<CreateEventParams>({
    companyId: '',
    name: '',
    description: '',
    heroImageId: '',
    hotelName: '',
    roomType: '',
    roomInclusions: '',
    checkinDate: '',
    checkoutDate: '',
    locationCity: '',
    locationCountry: '',
    itineraryHighlights: '',
    agendaFileUrl: '',
    dressCode: '',
    faqsUrl: '',
    isPublicLink: false,
    eventCode: '',
    allowEditRegistration: false,
    allowTripExtension: false,
    allowGuest: false,
    allowPreferredAirline: false,
    allowPreferredSeatClass: false,
  });

  const [initialFormData, setInitialFormData] = useState<CreateEventParams | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const heroImageUrl = useImageUrlGenerator(formData.heroImageId || '');

  useEffect(() => {
    if (event) {
      const eventData: CreateEventParams = {
        companyId: event.companyId,
        name: event.name,
        description: event.description || '',
        heroImageId: event.heroImageId || '',
        hotelName: event.hotelName || '',
        roomType: event.roomType || '',
        roomInclusions: event.roomInclusions || '',
        checkinDate: event.checkinDate || '',
        checkoutDate: event.checkoutDate || '',
        locationCity: event.locationCity || '',
        locationCountry: event.locationCountry || '',
        itineraryHighlights: event.itineraryHighlights || '',
        agendaFileUrl: event.agendaFileUrl || '',
        dressCode: event.dressCode || '',
        faqsUrl: event.faqsUrl || '',
        isPublicLink: event.isPublicLink,
        eventCode: event.eventCode || '',
        allowEditRegistration: event.allowEditRegistration,
        allowTripExtension: event.allowTripExtension || false,
        allowGuest: event.allowGuest || false,
        allowPreferredAirline: event.allowPreferredAirline || false,
        allowPreferredSeatClass: event.allowPreferredSeatClass || false,
      };
      setFormData(eventData);
      setInitialFormData(eventData);
    }
  }, [event]);

  // Detect unsaved changes
  useEffect(() => {
    if (!onHasUnsavedChangesChange) {
      return;
    }

    if (event && initialFormData) {
      // Edit mode: compare current form data with initial data
      const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
      onHasUnsavedChangesChange(hasChanges);
    } else if (!event) {
      // Create mode: check if any field has been filled
      const hasAnyData = Object.values(formData).some((value) => {
        if (typeof value === 'string') {
          return value.trim() !== '';
        }
        if (typeof value === 'boolean') {
          return value;
        }
        return false;
      });
      onHasUnsavedChangesChange(hasAnyData);
    }
  }, [formData, initialFormData, event, onHasUnsavedChangesChange]);

  const handleChange =
    (field: keyof CreateEventParams) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const handleCheckboxChange =
    (field: keyof CreateEventParams) => (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setFormData((prev) => ({ ...prev, [field]: checked }));
    };

  const handleDateChange = (field: 'checkinDate' | 'checkoutDate') => (value: Dayjs | null) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value ? value.format('YYYY-MM-DD') : '',
    }));
  };

  const handleImageUpload = useCallback(
    async (file: File) => {
      setIsUploadingImage(true);
      try {
        const response = await uploadImage(file);
        setFormData((prev) => ({ ...prev, heroImageId: response.public_id }));
        enqueueSnackbar('Image uploaded successfully', { variant: 'success' });
      } catch (error) {
        enqueueSnackbar(`Failed to upload image: ${error instanceof Error ? error.message : 'Unknown error'}`, {
          variant: 'error',
        });
      } finally {
        setIsUploadingImage(false);
      }
    },
    [enqueueSnackbar],
  );

  const handleRemoveImage = useCallback(() => {
    setFormData((prev) => ({ ...prev, heroImageId: '' }));
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSubmit(formData);
    // Clear unsaved changes flag
    if (event) {
      setInitialFormData(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Stack spacing={3}>
        <Grid2 container spacing={2}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Company"
              select
              value={formData.companyId}
              onChange={(e) => setFormData((prev) => ({ ...prev, companyId: e.target.value }))}
              required
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Event Name" value={formData.name} onChange={handleChange('name')} required />
          </Grid2>

          <Grid2 size={12}>
            <TextField
              fullWidth
              label="Description"
              value={formData.description}
              onChange={handleChange('description')}
              multiline
              rows={3}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Hotel Name" value={formData.hotelName} onChange={handleChange('hotelName')} />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Room Type" value={formData.roomType} onChange={handleChange('roomType')} />
          </Grid2>

          <Grid2 size={12}>
            <TextField
              fullWidth
              label="Room Inclusions"
              value={formData.roomInclusions}
              onChange={handleChange('roomInclusions')}
              multiline
              rows={2}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <DatePicker
              label="Check-in Date"
              value={formData.checkinDate ? newDate(formData.checkinDate) : null}
              onChange={handleDateChange('checkinDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <DatePicker
              label="Check-out Date"
              value={formData.checkoutDate ? newDate(formData.checkoutDate) : null}
              onChange={handleDateChange('checkoutDate')}
              slotProps={{
                textField: {
                  fullWidth: true,
                },
              }}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="City" value={formData.locationCity} onChange={handleChange('locationCity')} />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Country"
              value={formData.locationCountry}
              onChange={handleChange('locationCountry')}
            />
          </Grid2>

          <Grid2 size={12}>
            <TextField
              fullWidth
              label="Itinerary Highlights"
              value={formData.itineraryHighlights}
              onChange={handleChange('itineraryHighlights')}
              multiline
              rows={3}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Agenda File URL"
              value={formData.agendaFileUrl}
              onChange={handleChange('agendaFileUrl')}
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <TextField fullWidth label="Dress Code" value={formData.dressCode} onChange={handleChange('dressCode')} />
          </Grid2>

          <Grid2 size={12}>
            <TextField fullWidth label="FAQs URL" value={formData.faqsUrl} onChange={handleChange('faqsUrl')} />
          </Grid2>

          <Grid2 size={12}>
            <TextField
              fullWidth
              label="Event Code"
              value={formData.eventCode}
              onChange={handleChange('eventCode')}
              helperText="Required if using protected link"
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Checkbox checked={formData.isPublicLink} onChange={handleCheckboxChange('isPublicLink')} />}
              label="Public Link (no event code required)"
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowEditRegistration}
                  onChange={handleCheckboxChange('allowEditRegistration')}
                />
              }
              label="Allow employees to edit their registration"
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox checked={formData.allowTripExtension} onChange={handleCheckboxChange('allowTripExtension')} />
              }
              label="Allow Trip Extension"
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={<Checkbox checked={formData.allowGuest} onChange={handleCheckboxChange('allowGuest')} />}
              label="Allow Guest"
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowPreferredAirline}
                  onChange={handleCheckboxChange('allowPreferredAirline')}
                />
              }
              label="Allow Preferred Airline"
            />
          </Grid2>

          <Grid2 size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.allowPreferredSeatClass}
                  onChange={handleCheckboxChange('allowPreferredSeatClass')}
                />
              }
              label="Allow Preferred Seat Class"
            />
          </Grid2>

          <Grid2 size={12}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Hero Image
              </Typography>
              <Box
                sx={{
                  '& .MuiPaper-root': {
                    maxHeight: 400,
                    overflow: 'hidden',
                  },
                  '& .MuiPaper-root img': {
                    maxHeight: 400,
                    maxWidth: '100%',
                    objectFit: 'contain',
                  },
                }}
              >
                <ImageUploadBox
                  id="hero-image-upload"
                  name="heroImageId"
                  label="hero image"
                  imageUrl={heroImageUrl}
                  onUpload={handleImageUpload}
                  onRemove={handleRemoveImage}
                />
              </Box>
            </Box>
          </Grid2>
        </Grid2>

        <Box>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            {onCancel && (
              <Button variant="text" onClick={onCancel} disabled={isSubmitting || isUploadingImage}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting || isUploadingImage}
              startIcon={isUploadingImage ? <CircularProgress size={20} /> : undefined}
            >
              {isUploadingImage
                ? 'Uploading image...'
                : isSubmitting
                ? 'Saving...'
                : event
                ? 'Update Event'
                : 'Create Event'}
            </Button>
          </Stack>
        </Box>
      </Stack>
    </form>
  );
}
